/**
 * MatchDialog — the match tool (plan §7.5, §13.4 WP-33b frontend scope: "a
 * match dialog: type a text, see the exact match or the ranked candidates
 * with reasons, pick one (decide matched), create new (decide new_entry
 * with the new entry's fields), or reject"). No score, however high, is
 * ever auto-matched (G11) — POST /master/match never writes; a choice here
 * always becomes an explicit POST /master/decide.
 */
import { useState, FormEvent } from 'react';
import { Modal, fieldCls, FilterSelect, primaryBtnCls, secondaryBtnCls, outlineBtnCls, useToast } from '../../../../ui';
import { matchMaster, decideMaster } from '../../../api/client';
import type { MasterEntryRow, MasterListType, MasterListSection, MasterMatchResult, TagMatchCandidate } from '../../../api/client';
import { MASTER_LIST_TYPE_LABEL, MASTER_SECTION_OPTIONS } from './listTypes';

interface Props {
  isOpen: boolean;
  listType: MasterListType;
  onClose: () => void;
  /** Called whenever a decision creates or confirms an entry, so the parent can refresh its list. */
  onDecided: (entry: MasterEntryRow | null) => void;
}

export function MatchDialog({ isOpen, listType, onClose, onDecided }: Props) {
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [result, setResult] = useState<MasterMatchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [decidingKey, setDecidingKey] = useState<string | null>(null);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newOfficialName, setNewOfficialName] = useState('');
  const [newSection, setNewSection] = useState<'' | MasterListSection>('');

  function reset() {
    setText('');
    setResult(null);
    setSearchError(null);
    setShowCreateNew(false);
    setNewOfficialName('');
    setNewSection('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSearch(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSearching(true);
    setSearchError(null);
    setResult(null);
    try {
      const res = await matchMaster(listType, text.trim());
      setResult(res);
      setNewOfficialName(text.trim());
    } catch {
      setSearchError('Could not search the master list.');
    } finally {
      setSearching(false);
    }
  }

  async function pickCandidate(candidate: TagMatchCandidate) {
    setDecidingKey(candidate.entry.id);
    try {
      const decided = await decideMaster(listType, text.trim(), 'matched', { entryId: candidate.entry.id });
      toast(`Matched to ${candidate.entry.official_name}.`, { type: 'success' });
      onDecided(decided.entry);
      handleClose();
    } catch {
      toast('Could not record that match.', { type: 'error' });
    } finally {
      setDecidingKey(null);
    }
  }

  async function submitCreateNew(e: FormEvent) {
    e.preventDefault();
    if (!newOfficialName.trim()) return;
    setDecidingKey('create-new');
    try {
      const decided = await decideMaster(listType, text.trim(), 'new_entry', {
        newEntry: { official_name: newOfficialName.trim(), section: newSection || null },
      });
      toast(`${decided.entry?.official_name ?? 'Entry'} created.`, { type: 'success' });
      onDecided(decided.entry);
      handleClose();
    } catch {
      toast('Could not create the entry.', { type: 'error' });
    } finally {
      setDecidingKey(null);
    }
  }

  async function reject() {
    setDecidingKey('reject');
    try {
      await decideMaster(listType, text.trim(), 'rejected');
      toast('Recorded as not a list entry.', { type: 'info' });
      onDecided(null);
      handleClose();
    } catch {
      toast('Could not record that decision.', { type: 'error' });
    } finally {
      setDecidingKey(null);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Match — ${MASTER_LIST_TYPE_LABEL[listType]}`}>
      <div className="space-y-4">
        <form onSubmit={handleSearch} className="flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="cta-match-text" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Text to place
            </label>
            <input
              id="cta-match-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className={`${fieldCls} w-full`}
              autoFocus
              data-testid="match-text-input"
            />
          </div>
          <button type="submit" disabled={searching || !text.trim()} className={primaryBtnCls} data-testid="match-search-button">
            {searching ? 'Searching…' : 'Search'}
          </button>
        </form>

        {searchError && <p className="text-sm text-red-600 dark:text-red-400">{searchError}</p>}

        {result && result.status === 'matched' && (
          <div
            className="rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300"
            data-testid="match-exact-result"
          >
            Exact match on {result.matchLabel === 'alias' ? 'an alias' : 'the official name'}: <strong>{result.entry.official_name}</strong>.
            Nothing more to decide here.
          </div>
        )}

        {result && result.status === 'unmatched' && (
          <div className="space-y-3">
            {result.candidates.length > 0 ? (
              <div className="space-y-2" data-testid="match-candidates">
                <p className="text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Ranked candidates
                </p>
                {result.candidates.map((candidate) => (
                  <div
                    key={candidate.entry.id}
                    data-testid={`match-candidate-${candidate.entry.id}`}
                    className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{candidate.entry.official_name}</p>
                      <ul className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {candidate.reasons.map((reason, i) => (
                          <li key={i}>{reason.detail}</li>
                        ))}
                      </ul>
                    </div>
                    <button
                      type="button"
                      onClick={() => pickCandidate(candidate)}
                      disabled={decidingKey !== null}
                      className={`${outlineBtnCls()} flex-shrink-0`}
                      data-testid={`match-pick-${candidate.entry.id}`}
                    >
                      {decidingKey === candidate.entry.id ? 'Matching…' : 'Use this entry'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No close matches in the master list.</p>
            )}

            <div className="flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setShowCreateNew((v) => !v)}
                className={outlineBtnCls(showCreateNew)}
                data-testid="match-create-new-toggle"
              >
                Create new entry
              </button>
              <button
                type="button"
                onClick={reject}
                disabled={decidingKey !== null}
                className={secondaryBtnCls}
                data-testid="match-reject-button"
              >
                {decidingKey === 'reject' ? 'Recording…' : 'Not a list entry'}
              </button>
            </div>

            {showCreateNew && (
              <form onSubmit={submitCreateNew} className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid="match-create-new-form">
                <div>
                  <label htmlFor="cta-match-new-name" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    New entry name
                  </label>
                  <input
                    id="cta-match-new-name"
                    type="text"
                    value={newOfficialName}
                    onChange={(e) => setNewOfficialName(e.target.value)}
                    className={`${fieldCls} w-full`}
                    data-testid="match-create-new-name-input"
                  />
                </div>
                <div>
                  <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Section</span>
                  <FilterSelect
                    label="Section"
                    value={newSection}
                    onChange={(v) => setNewSection(v as '' | MasterListSection)}
                    options={MASTER_SECTION_OPTIONS}
                  />
                </div>
                <button
                  type="submit"
                  disabled={decidingKey !== null || !newOfficialName.trim()}
                  className={primaryBtnCls}
                  data-testid="match-create-new-submit"
                >
                  {decidingKey === 'create-new' ? 'Creating…' : 'Create and use'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default MatchDialog;
